import React from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-final-form'
import I18n from 'translations/i18n'
import { differenceInMinutes } from 'date-fns'
import { Trans } from 'react-i18next'
import { Text, Flex } from '@jisr-hr/design-system'
import {
  get24hour,
  increaseDecreaseTime,
  isNextDay,
  minuteToHumanReadable,
} from 'components/global/form/formNormalize'
import { composeValidators, invalidTime, required } from 'components/global/form/FormValidations'
import { TimePickerField } from 'components/final-form'

import Frame from '../../../ds/Frame'

import Styles from './EstimatedTime.module.css'
import { useRequest } from '../../../RequestProvider'

const estimateTypes = {
  late_in: I18n.t('estimated_clock_in_time'),
  early_out: I18n.t('estimated_clock_out_time'),
}

const durationType = {
  late_in: 'estimated_delay_duration',
  early_out: 'estimated_shortage_duration',
}

const EstimatedTime = ({ type, shift, recorded }) => {
  const { change } = useForm()
  const { handleDisableSubmit } = useRequest()

  const shiftStartWithFlexible = increaseDecreaseTime(
    shift?.shift_start_time,
    '+',
    shift?.flexible_duration || 0,
  )
  const shiftTime = type === 'late_in' ? shiftStartWithFlexible : shift?.expected_exit_time
  const time = shiftTime ? get24hour(shiftTime) : {}

  const duration = recorded ? get24hour(recorded) : {}
  const durationDay = isNextDay(shiftStartWithFlexible, recorded) ? 3 : 2
  let difference = 0
  if (type === 'late_in') {
    const isEqualTime = duration?.hour === time?.hour && duration?.minutes === (time?.minutes || 0)

    if (!isEqualTime) {
      difference = differenceInMinutes(
        new Date(2014, 6, durationDay, duration?.hour, duration?.minutes, 0),
        new Date(2014, 6, 2, time?.hour, time?.minutes || 0, 0),
      )
    }
  }

  if (type === 'early_out') {
    const outPunchNextDay = isNextDay(shift?.shiftStartWithFlexible, shift?.expected_exit_time)
      ? 3
      : 2

    difference = differenceInMinutes(
      new Date(2014, 6, outPunchNextDay, time?.hour, time?.minutes || 0, 0),
      new Date(2014, 6, durationDay, duration?.hour, duration?.minutes, 0),
    )
  }

  React.useEffect(() => {
    if (difference > 0) {
      change('estimateTime', difference)
      handleDisableSubmit(false)
    } else {
      handleDisableSubmit(true)
      change('estimateTime', null)
    }
  }, [difference])

  React.useEffect(() => {
    if (shiftTime) {
      change('duration', shiftTime)
    }
  }, [type, shift])

  React.useEffect(() => {
    change('isEstimate', true)
    return () => {
      change('isEstimate', false)
      change('duration', null)
      handleDisableSubmit(false)
    }
  }, [])

  const maxTime = isNextDay(shiftStartWithFlexible, shift?.expected_exit_time)
    ? null
    : shift?.expected_exit_time
  return (
    <Frame title={estimateTypes[type]}>
      <Flex
        flexCol
        className={Styles.estimatedTime}
      >
        <TimePickerField
          name="duration"
          validate={composeValidators(required, invalidTime)}
          minTime={maxTime ? shiftStartWithFlexible : null}
          maxTime={maxTime}
        />
        {difference > 0 && (
          <Text
            typography="paragraph14"
            color="#7c828c"
          >
            <Trans
              i18nKey={durationType[type]}
              components={{ b: <b style={{ color: '#000' }} /> }}
              values={{ minutes: `${minuteToHumanReadable(difference)}` }}
            />
          </Text>
        )}
      </Flex>
    </Frame>
  )
}

EstimatedTime.propTypes = {
  type: PropTypes.string,
  recorded: PropTypes.number,
  shift: PropTypes.shape(),
}

export default React.memo(EstimatedTime)
